<template>

    <div class="modal-section" :class="{ 'toggleable': isCollapsible || isSwitchable }">
        <div v-if="title" class="modal-section-header" :class="{ 'closed': show === false }" @click="toggle">
            <h2 class="modal-section-title"><i v-if="icon" :class="icon" />{{ title }}</h2>
            <div class="d-flex align-items-center gap-10">
                <v-badge-new v-if="date" :date="date" size="md" />
                <div v-if="isSwitchable" class="accessory" @click.stop>
                    <v-switch :model="show" @input="onSwitchInput" />
                </div>
                <div v-else-if="isCollapsible" class="accessory">
                    <div class="toggle">
                        <i v-if="show" class="fas fa-angle-up" />
                        <i v-else class="fas fa-angle-down" />
                    </div>
                </div>
            </div>
        </div>
        <div v-show="show">
            <div v-if="$slots['body']" class="modal-section-body" :class="{ padded: padded }">
                <slot name="body"></slot>
            </div>
            <div v-else class="modal-section-body" :class="{ padded: padded }">
                <slot></slot>
            </div>
            <div v-if="$slots['footer']" class="modal-section-footer">
                <slot name="footer"></slot>
            </div>
        </div>
    </div>

</template>

<style scoped lang="scss">

    .modal-section {
        border: 1px solid $color-rule;
        border-radius: 5px;
        margin-bottom: 15px;
        position: relative;
        &:last-child {
            margin: 0;
        }
        &.toggleable .modal-section-header {
            cursor: pointer;
        }
    }

    .modal-section-header {
        align-items: center;
        background: $color-white;
        border-top-left-radius: 5px;
        border-top-right-radius: 5px;
        display: flex;
        height: 50px;
        justify-content: space-between;
        padding: 0 15px;
        &:hover .toggle {
            opacity: 1;
        }
        &.closed {
            border: none;
            border-radius: 5px;
            overflow: hidden;
        }
        .toggle {
            align-items: center;
            color: $color-text;
            display: flex;
            font-size: 16px;
            height: 100%;
            justify-content: center;
            min-width: 30px;
            opacity: 0.5;
            transition: .3s opacity;
            &:hover {
                opacity: 1;
            }
        }
    }

    .modal-section-title {
        @include font-semibold(14px);
        color: $color-text;
        margin: 0;
        padding: 0;
        text-align: left;
        user-select: none;
        i {
            margin-right: 5px;
            text-align: center;
            width: 20px;
        }
    }

    .modal-section-body {
        &.padded {
            padding: 20px;
        }
        > *:last-child {
            margin-bottom: 0;
        }
        p {
            @include font-regular(14px);
            color: $color-text;
            line-height: 1.6em;
            margin: 0 0 15px 0;
            text-align: center;
            &.small {
                font-size: 13px;
            }
        }
        ul::v-deep {
            list-style: none;
            margin: 0 0 15px 0;
            padding: 0;
            text-align: left;
            li {
                &::before {
                    content: "\f061";
                    color: $color-muted;
                    flex-shrink: 0;
                    font-family: 'Font Awesome 7 Pro';
                    margin-right: 10px;
                }
                @include font-regular(14px);
                align-items: flex-start;
                color: $color-text;
                display: flex;
                line-height: 1.6em;
                margin: 0;
                padding: 0;
            }
            li.no-bullet {
                &::before {
                    content: none;
                }
            }
        }
    }

    .modal-section-footer {
        border-top: 1px solid $color-rule;
        display: flex;
        justify-content: space-between;
        padding: 10px;
        *:only-child {
            margin-left: auto;
        }
    }

</style>

<script>

import BadgeNew from '@cmp/BadgeNew';

export default {

    components: {
        'v-badge-new': BadgeNew,
    },

    props: {
        // No Boolean type — omitted must stay undefined (Boolean defaults to false).
        collapsed: {
            default: undefined,
        },
        date: {
            type: String,
            validator: function(value) {
                return /^\d{4}-\d{2}-\d{2}$/.test(value);
            },
        },
        icon: {
            type: String,
        },
        inactive: {
            default: undefined,
        },
        padded: {
            type: Boolean,
            default: true,
        },
        title: {
            type: String,
        },
    },

    data() {
        return {
            show: true,
        }
    },

    computed: {

        isCollapsible() {
            return this.collapsed !== undefined;
        },

        isSwitchable() {
            return this.inactive !== undefined;
        },

    },

    watch: {
        collapsed: {
            immediate: true,
            handler() {
                this.syncVisibility();
            },
        },
        inactive: {
            immediate: true,
            handler() {
                this.syncVisibility();
            },
        },
    },

    methods: {

        onSwitchInput(val) {
            this.show = val;
            this.$emit('input', val);
        },

        syncVisibility() {
            if (this.isSwitchable) {
                this.show = !this.inactive;
            } else if (this.isCollapsible) {
                this.show = this.collapsed === false;
            } else {
                // Default / reused after a collapsed section: always expanded.
                this.show = true;
            }
        },

        toggle() {
            if (this.isCollapsible) {
                this.show = !this.show;
            }
        },

    },

}

</script>
